import React, { useEffect, useState } from 'react';
import {
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Modal
} from '@material-ui/core';
import { useForm, UseFormReturn, FormProvider } from 'react-hook-form';
import { TAddFolderForm } from '../../types/cloudStorage/CloudFileList';
import { AppTextfield } from '../parts/input/AppTextfield';
import { AppSelectfield } from '../parts/input/AppSelectfield';
import { TSelectItem } from '../../types/common';
import { createPictureFolder } from '../../apicalls/cloudFile';
import { useHistory } from 'react-router-dom';
import {
  ModalCancelButton,
  ModalSubmitButton
} from '../parts/cloudStorage/style/style';
import { checkResData } from '../../utils/common';
import { useRecoilState } from 'recoil';
import { openErrorFlg, openSuccessFlg, resMessage } from '../../GlobalState';
import { useSnackbar } from 'notistack';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

type Tprops = {
  selectItem: TSelectItem[];
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  getFolderPictureList: any;
  constructIdDef: TSelectItem;
};

// 初期データ
const defaultValues: TAddFolderForm = {
  name: '',
  construct_id: { value: 0, label: '--' }
};

/**
 * クラウドストレージ用共通モーダル
 * @param props
 * @constructor
 */
export const FolderModal: React.FC<Tprops> = (props: Tprops) => {
  const {
    selectItem,
    modalOpen,
    setModalOpen,
    getFolderPictureList,
    constructIdDef
  } = props;

  const classes = useStyles();
  const history = useHistory();

  const { enqueueSnackbar } = useSnackbar();

  const [isDisabled, setIsDisabled] = useState<boolean>(true);

  // hook-formの初期設定
  const methods: UseFormReturn<TAddFolderForm, any> = useForm({
    defaultValues
  });
  const {
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors }
  } = methods;

  useEffect(() => {
    setValue('construct_id', constructIdDef);
  }, [constructIdDef?.value]);

  const onChangeName = (value: any) => {
    const constructId: TSelectItem | undefined = getValues('construct_id');
    setIsDisabled(
      constructId && constructId.value != 0 && value.target.value ? false : true
    );
  };

  const onChangeConstructId = (value: TSelectItem | undefined) => {
    setIsDisabled(
      value && value.value != 0 && getValues('name') ? false : true
    );
  };

  const onSend = () => {
    const params = {
      construct_id: getValues('construct_id').value,
      name: getValues('name')
    };
    createPictureFolder(params).then(async resData => {
      checkResData(resData, enqueueSnackbar).then(async (data: any) => {
        await getFolderPictureList();
        setModalOpen(false);
        reset({
          construct_id: constructIdDef,
          name: ''
        });
        setIsDisabled(true);
      });
    });
  };

  // サイズ調整
  const theme = useTheme();
  const isSmDownScreen = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <FormProvider {...methods}>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby='simple-modal-title'
        aria-describedby='simple-modal-description'
        className={classes.modal}
      >
        <div className={classes.paper}>
          <Grid container>
            <Grid item xs={12} style={{ padding: 36 }}>
              <div style={{ textAlign: 'center' }}>
                <h1 style={{ margin: 24, fontSize: isSmDownScreen ? 24 : 20 }}>
                  フォルダを追加
                </h1>
                <div className={classes.container}>
                  <div>
                    <div
                      className={classes.container}
                      style={{
                        margin: 20,
                        display: 'flex',
                        flexDirection: isSmDownScreen ? 'row' : 'column'
                      }}
                    >
                      <div
                        style={{
                          marginRight: 24,
                          marginBottom: isSmDownScreen ? 0 : 5
                        }}
                      >
                        フォルダ名
                      </div>
                      <AppTextfield
                        name='name'
                        style={{ width: isSmDownScreen ? 360 : 240 }}
                        required={true}
                        errorMessage={errors?.name?.message}
                        onChange={onChangeName}
                        maxLength={20}
                      />
                    </div>
                    <div
                      className={classes.container}
                      style={{
                        margin: 20,
                        display: 'flex',
                        flexDirection: isSmDownScreen ? 'row' : 'column'
                      }}
                    >
                      <div
                        style={{
                          width: '100px',
                          marginBottom: isSmDownScreen ? 0 : 5
                        }}
                      >
                        案件名<span style={{ color: 'red' }}>(必須)</span>
                      </div>
                      <AppSelectfield
                        name='construct_id'
                        listDatas={selectItem}
                        value={constructIdDef}
                        style={{ width: isSmDownScreen ? 360 : 240 }}
                        required={true}
                        error={'construct_id' in errors}
                        helperText={errors?.construct_id && '必須です。'}
                        onChange={onChangeConstructId}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} className={classes.modalFooter}>
              <ModalCancelButton
                variant='contained'
                onClick={() => {
                  reset({
                    construct_id: constructIdDef,
                    name: ''
                  });
                  setModalOpen(false);
                  setIsDisabled(true);
                }}
              >
                キャンセル
              </ModalCancelButton>
              <ModalSubmitButton
                variant='contained'
                onClick={handleSubmit(onSend)}
                disabled={isDisabled}
              >
                追加する
              </ModalSubmitButton>
            </Grid>
          </Grid>
        </div>
      </Modal>
    </FormProvider>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },

    paper: {
      // モーダル内部
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.spacing(2),
      boxShadow: theme.shadows[5],
      width: '70%',
      overflow: 'hidden'
    },

    modalFooter: {
      // モーダル下部
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderTop: '1px solid #cfcfcf',
      padding: 30
    },
    container: {
      // モーダルを中央に
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  })
);
